import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ChevronRight, Save, FileDown, BarChart3, CheckCircle2, Link2, Copy, Trash2, Plus, QrCode, X, Mail, Send, CalendarClock } from "lucide-react";

const GROUP_LABELS = {
    fib: "Felicidade Interna Bruta (FIB)",
    whr: "World Happiness Report (WHR)",
    global: "Geral",
    custom: "Personalizadas",
};

export default function SurveyFillPage() {
    const { id: pid, phase } = useParams();
    const phaseNum = Number(phase) || 1;
    const navigate = useNavigate();
    const { user } = useAuth();

    const [questions, setQuestions] = useState([]);
    const [indices, setIndices] = useState([]);
    const [responses, setResponses] = useState({});
    // The user's already-saved responses (server-side snapshot). We use this
    // to (a) reveal the "Editar respostas" button when the user has already
    // submitted and (b) lock the form as read-only until they explicitly
    // choose to edit — avoids accidental overwrites.
    const [savedResponses, setSavedResponses] = useState({});
    const [editing, setEditing] = useState(false);
    const [closesAt, setClosesAt] = useState(null); // ISO string or null
    // Managers keep survey access after the deadline; this flag drives an
    // explicit warning banner so they know the survey is technically closed
    // for the collaborator population.
    const [closedForCollaborators, setClosedForCollaborators] = useState(false);
    const [open, setOpen] = useState({ fib: true, whr: true, global: true, custom: true });
    const [busy, setBusy] = useState(false);
    const [links, setLinks] = useState([]);
    const [showLinkForm, setShowLinkForm] = useState(false);
    const [newLink, setNewLink] = useState({ expires_at: "", max_responses: "" });
    const [creatingLink, setCreatingLink] = useState(false);
    const [qrLink, setQrLink] = useState(null); // {id, token} of link being shown in QR modal
    const [emailLink, setEmailLink] = useState(null); // {id, token} of link to email
    const [emailTeam, setEmailTeam] = useState([]); // list of {id, name, email}
    const [emailSelected, setEmailSelected] = useState(new Set());
    const [emailMessage, setEmailMessage] = useState("");
    const [emailSending, setEmailSending] = useState(false);

    const canManageLinks = user && ["super_admin", "company_manager", "happiness_manager", "project_manager"].includes(user.role);

    const loadLinks = useCallback(() => {
        if (!canManageLinks) return;
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/links`)
            .then((r) => setLinks(r.data || []))
            .catch((err) => {
                console.warn("[survey] links fetch suppressed:", err?.message || err);
            });
    }, [pid, phaseNum, canManageLinks]);
    const loadQuestionsAndResponses = useCallback(() => {
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/questions`)
            .then((r) => {
                setQuestions(r.data.questions || []);
                setIndices(r.data.indices || []);
                setClosesAt(r.data.closes_at || null);
                setClosedForCollaborators(!!r.data.closed_for_collaborators);
                if (r.data.closed) {
                    navigate(`/app/projects/${pid}/surveys/${phaseNum}/closed?closed_at=${encodeURIComponent(r.data.closes_at || "")}`, { replace: true });
                }
            }).catch((err) => toast.error(formatApiError(err)));
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/my-responses`)
            .then((r) => {
                const map = {};
                for (const x of r.data || []) map[x.question_id] = x.value;
                setResponses(map);
                setSavedResponses(map);
                // If the user has NOT submitted anything yet, start in edit mode.
                setEditing(Object.keys(map).length === 0);
            }).catch((err) => {
                // pre-existing responses are optional — non-fatal.
                console.warn("[survey] my-responses fetch suppressed:", err?.message || err);
                setEditing(true);
            });
    }, [pid, phaseNum, navigate]);

    useEffect(() => { loadQuestionsAndResponses(); }, [loadQuestionsAndResponses]);
    useEffect(() => { loadLinks(); }, [loadLinks]);

    // Poll the anonymous-link list every 30s so the response counters update
    // in near real-time without a page refresh. Uses document.visibilityState
    // to pause when the tab is hidden.
    useEffect(() => {
        if (!canManageLinks) return undefined;
        const tick = () => {
            if (document.visibilityState === "visible") loadLinks();
        };
        const timer = setInterval(tick, 30 * 1000);
        return () => clearInterval(timer);
    }, [canManageLinks, loadLinks]);

    const publicBase = `${window.location.origin}/s/`;

    const createLink = async () => {
        setCreatingLink(true);
        try {
            const payload = {};
            if (newLink.expires_at) payload.expires_at = newLink.expires_at;
            if (newLink.max_responses) payload.max_responses = Number(newLink.max_responses);
            await api.post(`/projects/${pid}/surveys/phases/${phaseNum}/links`, payload);
            toast.success("Link anónimo criado");
            setShowLinkForm(false);
            setNewLink({ expires_at: "", max_responses: "" });
            loadLinks();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setCreatingLink(false); }
    };

    const toggleLink = async (link) => {
        try {
            await api.patch(`/survey-links/${link.id}`, { active: !link.active });
            loadLinks();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeLink = async (link) => {
        if (!window.confirm("Eliminar este link? As respostas já submetidas não são afetadas.")) return;
        try {
            await api.delete(`/survey-links/${link.id}`);
            toast.success("Link eliminado");
            loadLinks();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const copyLink = async (token) => {
        try {
            await navigator.clipboard.writeText(`${publicBase}${token}`);
            toast.success("Link copiado para a área de transferência");
        } catch {
            toast.error("Não foi possível copiar — copie manualmente");
        }
    };

    const openEmailModal = async (link) => {
        setEmailLink(link);
        setEmailSelected(new Set());
        setEmailMessage("");
        try {
            const r = await api.get("/users");
            const list = (r.data || []).filter((u) => u.email && u.id !== user?.id);
            setEmailTeam(list);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleEmailSel = (id) => {
        setEmailSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleEmailSelAll = () => {
        setEmailSelected((prev) => (prev.size === emailTeam.length ? new Set() : new Set(emailTeam.map((u) => u.id))));
    };

    const sendEmail = async () => {
        if (!emailLink) return;
        if (emailSelected.size === 0) {
            toast.error("Selecione pelo menos um destinatário");
            return;
        }
        setEmailSending(true);
        try {
            const r = await api.post(`/survey-links/${emailLink.id}/send-email`, {
                user_ids: Array.from(emailSelected),
                message: emailMessage || null,
            });
            const { sent, total, errors } = r.data || {};
            if (sent > 0) {
                toast.success(`${sent}/${total} email(s) enviados`);
            }
            if (errors && errors.length) {
                toast.error(`${errors.length} falha(s) — verifique a configuração SMTP`);
            }
            setEmailLink(null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setEmailSending(false);
        }
    };

    const grouped = useMemo(() => {
        const g = {};
        for (const q of questions) {
            const key = q.group || "global";
            (g[key] ||= []).push(q);
        }
        for (const k of Object.keys(g)) g[k].sort((a, b) => a.order - b.order);
        return g;
    }, [questions]);

    const allMandatory = useMemo(
        () => questions.filter((q) => q.mandatory).map((q) => q.id),
        [questions]
    );
    const answered = useMemo(
        () => allMandatory.filter((id) => responses[id] !== undefined && responses[id] !== "" && !(Array.isArray(responses[id]) && responses[id].length === 0)),
        [allMandatory, responses]
    );
    const progress = allMandatory.length === 0 ? 100 : Math.round((answered.length / allMandatory.length) * 100);

    const submit = async () => {
        const missing = allMandatory.filter((id) => !(responses[id] !== undefined && responses[id] !== ""));
        if (missing.length) {
            toast.error(`Faltam ${missing.length} resposta(s) obrigatória(s)`);
            const firstQ = questions.find((q) => q.id === missing[0]);
            if (firstQ) {
                const el = document.querySelector(`[data-testid="q-${firstQ.id}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setBusy(true);
        try {
            const payload = {
                responses: Object.entries(responses)
                    .filter(([, v]) => v !== undefined && v !== "")
                    .map(([question_id, value]) => ({ question_id, value })),
            };
            await api.post(`/projects/${pid}/surveys/phases/${phaseNum}/responses`, payload);
            toast.success("Respostas submetidas");
            setSavedResponses({ ...responses });
            setEditing(false);
        } catch (err) {
            // Deadline expired mid-fill — server responds 410 with a stable code.
            if (err?.response?.status === 410 && err.response.data?.detail?.code === "survey_closed") {
                const closedAtIso = err.response.data.detail.closed_at || "";
                navigate(`/app/projects/${pid}/surveys/${phaseNum}/closed?closed_at=${encodeURIComponent(closedAtIso)}`, { replace: true });
                return;
            }
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    // Central write-guard: while `editing` is false the form must behave as
    // read-only. We short-circuit setVal (which is also the sink for
    // toggleMulti and every question type), so no answer can be mutated
    // regardless of which control the user interacts with. The visible
    // submit button is also disabled — this is defence in depth.
    const setVal = (qid, v) => {
        if (!editing) return;
        setResponses({ ...responses, [qid]: v });
    };
    const toggleMulti = (qid, opt) => {
        if (!editing) return;
        const cur = Array.isArray(responses[qid]) ? responses[qid] : [];
        setVal(qid, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
    };

    return (
        <div className="space-y-8" data-testid="survey-fill-page">
            <div>
                <Link to={`/app/projects/${pid}`} className="label-caps inline-flex items-center gap-2" data-testid="back-to-project">
                    <ArrowLeft size={12} /> Projeto
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
                    <div>
                        <h1 className="font-display text-5xl text-[#0d1628]">Inquérito · Fase {phaseNum}</h1>
                        <p className="text-[#52525b] mt-3 max-w-3xl">
                            Responda às {questions.length} perguntas agrupadas por dimensão da felicidade.
                            Apenas as obrigatórias (com *) bloqueiam a submissão.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href={`${API}/projects/${pid}/surveys/phases/${phaseNum}/pdf`} target="_blank" rel="noreferrer"
                            className="btn-outline" data-testid="pdf-blank-btn">
                            <FileDown size={14} /> PDF em branco
                        </a>
                        <Link to={`/app/projects/${pid}/surveys/${phaseNum}/statistics`} className="btn-outline" data-testid="stats-link">
                            <BarChart3 size={14} /> Estatísticas
                        </Link>
                    </div>
                </div>
            </div>

            {/* Progress bar */}
            <div className="border border-[#e4e4e7] p-4" data-testid="survey-progress">
                <div className="flex items-center justify-between text-sm">
                    <span className="label-caps">Progresso · {answered.length} / {allMandatory.length} obrigatórias</span>
                    <span className="font-display text-2xl text-[#0d1628]">{progress}%</span>
                </div>
                <div className="mt-3 h-1.5 bg-[#e4e4e7]">
                    <div className="h-full bg-[#b89b6f] transition-all duration-500" style={{ width: `${progress}%` }}></div>
                </div>
            </div>

            {closesAt && !closedForCollaborators && (
                <div className="border border-[#e4d9c0] bg-[#faf6ea] p-4 flex items-start gap-3" data-testid="survey-deadline-banner">
                    <CalendarClock size={18} className="text-[#b89b6f] mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm text-[#52525b]">
                        <span className="text-[#0d1628] font-medium">Prazo do inquérito:</span>{" "}
                        {new Date(closesAt).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}
                        <span className="ml-2 text-[#71717a]">— pode editar as suas respostas até esta data.</span>
                    </div>
                </div>
            )}

            {closedForCollaborators && (
                <div className="border border-[#f5cfcf] bg-[#faeeee] p-4 flex items-start gap-3" data-testid="survey-closed-manager-banner">
                    <CalendarClock size={18} className="text-[#dc2626] mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm text-[#8a1c1c]">
                        <span className="font-medium">Inquérito encerrado para colaboradores.</span>{" "}
                        Está a ver como gestor — pode consultar e ajustar respostas, mas os colaboradores já não conseguem submeter.
                    </div>
                </div>
            )}

            {Object.keys(savedResponses).length > 0 && !editing && (
                <div className="border border-[#c7e8d9] bg-[#eef8f3] p-4 flex items-start gap-3" data-testid="survey-saved-banner">
                    <CheckCircle2 size={18} className="text-[#118567] mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm text-[#0d1628] font-medium">Já submeteu {Object.keys(savedResponses).length} resposta(s)</div>
                        <div className="text-sm text-[#52525b] mt-1">
                            As suas respostas ficam guardadas. Pode voltar sempre que quiser para alterar antes do prazo terminar.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="btn-outline text-sm whitespace-nowrap"
                        data-testid="edit-responses-btn"
                    >
                        Editar respostas
                    </button>
                </div>
            )}

            {/* Anonymous Survey Links — managers only */}
            {canManageLinks && (
                <section className="border border-[#e4e4e7] p-5 bg-[#f5f3ee]" data-testid="anon-links-panel">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="label-caps flex items-center gap-2"><Link2 size={14} /> Links anónimos</div>
                            <p className="text-sm text-[#52525b] mt-1.5 max-w-2xl">
                                Partilhe um link público para receber respostas <strong>anónimas</strong> a este inquérito — útil para colaboradores sem conta, parceiros ou stakeholders externos. As respostas contam para as estatísticas do projeto.
                            </p>
                        </div>
                        <button type="button" onClick={() => setShowLinkForm(!showLinkForm)} className="btn-outline" data-testid="new-link-btn">
                            <Plus size={14} /> Novo link
                        </button>
                    </div>

                    {showLinkForm && (
                        <div className="mt-5 grid sm:grid-cols-3 gap-4 items-end border-t border-[#e4e4e7] pt-5" data-testid="new-link-form">
                            <div>
                                <label className="label-caps block mb-2">Expira em (opcional)</label>
                                <input type="date" className="input-clean" value={newLink.expires_at}
                                    onChange={(e) => setNewLink({ ...newLink, expires_at: e.target.value })}
                                    min={new Date().toISOString().slice(0, 10)}
                                    data-testid="new-link-expires" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Limite de respostas (opcional)</label>
                                <input type="number" min="1" className="input-clean" placeholder="Sem limite"
                                    value={newLink.max_responses}
                                    onChange={(e) => setNewLink({ ...newLink, max_responses: e.target.value })}
                                    data-testid="new-link-max" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={createLink} disabled={creatingLink} className="btn-primary disabled:opacity-50" data-testid="new-link-create">
                                    {creatingLink ? "A criar..." : "Criar link"}
                                </button>
                                <button type="button" onClick={() => setShowLinkForm(false)} className="btn-ghost">Cancelar</button>
                            </div>
                        </div>
                    )}

                    {links.length > 0 && (
                        <div className="mt-5 space-y-2" data-testid="anon-links-list">
                            {links.map((lk) => {
                                const status = lk.status || {};
                                const usable = status.usable;
                                const statusLabel = status.expired ? "Expirado"
                                    : status.exhausted ? "Limite atingido"
                                    : !status.active ? "Desativado"
                                    : "Ativo";
                                const statusClass = usable ? "bg-[#10b981]/15 text-[#065f46]"
                                    : "bg-[#dc2626]/15 text-[#7f1d1d]";
                                const publicUrl = `${publicBase}${lk.token}`;
                                const answered = lk.responses_count || 0;
                                const expected = lk.expected_recipients || 0;
                                const pct = expected ? Math.min(100, Math.round((answered / expected) * 100)) : 0;
                                return (
                                    <div key={lk.id} className="bg-white border border-[#e4e4e7] p-3" data-testid={`anon-link-${lk.id}`}>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <code className="text-xs text-[#0d1628] truncate max-w-md flex-1">{publicUrl}</code>
                                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${statusClass}`}>{statusLabel}</span>
                                            {expected > 0 ? (
                                                <span className="text-xs text-[#52525b]" data-testid={`anon-link-progress-${lk.id}`}>
                                                    <strong className="text-[#0d1628]">{answered}</strong> de <strong className="text-[#0d1628]">{expected}</strong> {answered === 1 ? "respondeu" : "responderam"} · <strong className="text-[#b89b6f]">{pct}%</strong>
                                                </span>
                                            ) : (
                                                <span className="text-xs text-[#52525b]">
                                                    {answered} resp.
                                                </span>
                                            )}
                                            {lk.expires_at && (
                                                <span className="text-xs text-[#52525b]">expira {new Date(lk.expires_at).toLocaleDateString("pt-PT")}</span>
                                            )}
                                            <div className="flex gap-1 ml-auto">
                                                <button type="button" onClick={() => copyLink(lk.token)} className="btn-ghost text-xs" title="Copiar" data-testid={`anon-link-copy-${lk.id}`}>
                                                    <Copy size={12} /> Copiar
                                                </button>
                                                <button type="button" onClick={() => setQrLink(lk)} className="btn-ghost text-xs" title="QR Code" data-testid={`anon-link-qr-${lk.id}`}>
                                                    <QrCode size={12} /> QR
                                                </button>
                                                <button type="button" onClick={() => openEmailModal(lk)} className="btn-ghost text-xs" title="Enviar por email a colaboradores" data-testid={`anon-link-email-${lk.id}`}>
                                                    <Mail size={12} /> Enviar por email
                                                </button>
                                                <button type="button" onClick={() => toggleLink(lk)} className="btn-ghost text-xs" data-testid={`anon-link-toggle-${lk.id}`}>
                                                    {lk.active ? "Desativar" : "Ativar"}
                                                </button>
                                                <button type="button" onClick={() => removeLink(lk)} className="btn-ghost text-xs text-[#dc2626]" data-testid={`anon-link-delete-${lk.id}`}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </div>
                                        {expected > 0 && (
                                            <div className="mt-2 h-1 bg-[#e4e4e7]" data-testid={`anon-link-bar-${lk.id}`}>
                                                <div className="h-full bg-[#b89b6f] transition-all duration-500" style={{ width: `${pct}%` }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!links.length && !showLinkForm && (
                        <p className="text-xs text-[#52525b] mt-4">Ainda não criou nenhum link anónimo para esta fase.</p>
                    )}
                </section>
            )}

            <div className={Object.keys(savedResponses).length > 0 && !editing ? "opacity-70 pointer-events-none" : ""} data-testid="survey-questions-container">
            {["fib", "whr", "global", "custom"].map((g) => {
                const items = grouped[g];
                if (!items?.length) return null;
                return (
                    <section key={g} data-testid={`section-${g}`}>
                        <button
                            type="button"
                            onClick={() => setOpen({ ...open, [g]: !open[g] })}
                            className="w-full flex items-center justify-between text-left border border-[#e4e4e7] px-5 py-4 bg-[#f5f3ee] hover:bg-[#ece8de] transition-colors"
                            data-testid={`section-toggle-${g}`}
                        >
                            <div className="flex items-center gap-3">
                                {open[g] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                <span className="label-caps">{GROUP_LABELS[g] || g}</span>
                                <span className="text-xs text-[#52525b]">({items.length} perguntas)</span>
                            </div>
                            <span className="text-xs text-[#52525b]">
                                {items.filter((q) => responses[q.id] !== undefined && responses[q.id] !== "").length}/{items.length} respondidas
                            </span>
                        </button>
                        {open[g] && (
                            <div className="border border-t-0 border-[#e4e4e7] divide-y divide-[#e4e4e7]">
                                {items.map((q) => (
                                    <QuestionRow
                                        key={q.id}
                                        question={q}
                                        value={responses[q.id]}
                                        indices={indices}
                                        onChange={(v) => setVal(q.id, v)}
                                        onToggleMulti={(o) => toggleMulti(q.id, o)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}
            </div>

            <div className="sticky bottom-0 bg-[#fdfcf9] border-t border-[#e4e4e7] py-4 mt-8 -mx-4 px-4 flex justify-end gap-3">
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy || !editing}
                    className="btn-primary"
                    data-testid="submit-survey-btn"
                    title={!editing ? "Clique em \"Editar respostas\" para poder submeter novamente" : ""}
                >
                    <Save size={14} /> {busy ? "A guardar..." : (Object.keys(savedResponses).length > 0 ? "Guardar alterações" : "Submeter respostas")}
                </button>
            </div>

            {qrLink && (
                <div
                    className="fixed inset-0 z-50 bg-[#0d1628]/80 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => setQrLink(null)}
                    data-testid="qr-modal"
                >
                    <div
                        className="bg-white border border-[#e4e4e7] max-w-md w-full p-8 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setQrLink(null)}
                            className="absolute top-3 right-3 text-[#52525b] hover:text-[#0d1628]"
                            aria-label="Fechar"
                            data-testid="qr-modal-close"
                        >
                            <X size={18} />
                        </button>
                        <div className="label-caps">Código QR · Fase {phaseNum}</div>
                        <h3 className="font-display text-2xl text-[#0d1628] mt-2">Partilhe o inquérito</h3>
                        <p className="text-xs text-[#52525b] mt-2">
                            Imprima ou projete este QR code. Quem o leia abre diretamente o inquérito anónimo, sem precisar de conta.
                        </p>
                        <div className="mt-6 flex justify-center bg-white p-4 border border-[#e4e4e7]">
                            <img
                                src={`${API}/survey-links/${qrLink.id}/qr.png`}
                                alt="QR code para inquérito anónimo"
                                className="w-64 h-64"
                                data-testid="qr-image"
                            />
                        </div>
                        <code className="block text-xs text-center text-[#0d1628] mt-4 break-all" data-testid="qr-url">
                            {publicBase}{qrLink.token}
                        </code>
                        <div className="flex gap-2 mt-5">
                            <button
                                type="button"
                                onClick={() => copyLink(qrLink.token)}
                                className="btn-outline flex-1"
                                data-testid="qr-modal-copy"
                            >
                                <Copy size={14} /> Copiar link
                            </button>
                            <a
                                href={`${API}/survey-links/${qrLink.id}/qr.png`}
                                download={`qr-inquerito-fase-${phaseNum}.png`}
                                className="btn-primary flex-1 text-center"
                                data-testid="qr-modal-download"
                            >
                                <FileDown size={14} /> Descarregar PNG
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {emailLink && (
                <div
                    className="fixed inset-0 z-50 bg-[#0d1628]/80 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => setEmailLink(null)}
                    data-testid="email-modal"
                >
                    <div
                        className="bg-white border border-[#e4e4e7] max-w-lg w-full p-8 relative max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setEmailLink(null)}
                            className="absolute top-3 right-3 text-[#52525b] hover:text-[#0d1628]"
                            aria-label="Fechar"
                            data-testid="email-modal-close"
                        >
                            <X size={18} />
                        </button>
                        <div className="label-caps">Enviar link · Fase {phaseNum}</div>
                        <h3 className="font-display text-2xl text-[#0d1628] mt-2">Convidar colaboradores</h3>
                        <p className="text-xs text-[#52525b] mt-2">
                            O email inclui um botão para abrir o inquérito anónimo. Cada destinatário abre o mesmo link partilhado.
                        </p>

                        <div className="mt-5 flex items-center justify-between text-xs">
                            <button type="button" onClick={toggleEmailSelAll} className="link-underline text-[#0d1628]" data-testid="email-toggle-all">
                                {emailSelected.size === emailTeam.length && emailTeam.length > 0 ? "Desmarcar todos" : "Selecionar todos"}
                            </button>
                            <span className="text-[#52525b]">{emailSelected.size} / {emailTeam.length} selecionados</span>
                        </div>

                        <div className="mt-3 flex-1 overflow-y-auto border border-[#e4e4e7]" data-testid="email-team-list">
                            {emailTeam.length === 0 && (
                                <div className="p-4 text-sm text-[#52525b] text-center">Sem outros utilizadores com email na sua empresa.</div>
                            )}
                            <ul className="divide-y divide-[#e4e4e7]">
                                {emailTeam.map((u) => (
                                    <li key={u.id} className="px-3 py-2 flex items-center gap-3" data-testid={`email-team-${u.id}`}>
                                        <input
                                            type="checkbox"
                                            checked={emailSelected.has(u.id)}
                                            onChange={() => toggleEmailSel(u.id)}
                                            className="cursor-pointer"
                                            data-testid={`email-team-check-${u.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-[#0d1628] truncate">{u.name || "—"}</div>
                                            <div className="text-xs text-[#52525b] truncate">{u.email}</div>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider text-[#52525b]">{u.role}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="mt-4">
                            <label className="label-caps block mb-2 text-[#52525b]">Mensagem pessoal (opcional)</label>
                            <textarea
                                rows={3}
                                className="input-clean w-full"
                                value={emailMessage}
                                onChange={(e) => setEmailMessage(e.target.value)}
                                placeholder="Ex: A vossa opinião é essencial para o projeto de bem-estar. Obrigado!"
                                data-testid="email-message"
                            />
                        </div>

                        <div className="mt-5 flex gap-2 justify-end">
                            <button type="button" onClick={() => setEmailLink(null)} className="btn-ghost">Cancelar</button>
                            <button
                                type="button"
                                onClick={sendEmail}
                                disabled={emailSending || emailSelected.size === 0}
                                className="btn-primary disabled:opacity-50"
                                data-testid="email-send-btn"
                            >
                                <Send size={14} /> {emailSending ? "A enviar..." : `Enviar (${emailSelected.size})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function QuestionRow({ question: q, value, indices, onChange, onToggleMulti }) {
    const idxs = (q.indices || []).map((id) => indices.find((i) => i.id === id)?.name).filter(Boolean);
    return (
        <div className="p-5" data-testid={`q-${q.id}`}>
            <div className="flex items-start gap-3 mb-3">
                <span className="text-[#b89b6f] font-display text-lg w-8 flex-shrink-0">{q.order}.</span>
                <div className="flex-1">
                    <div className="text-[#0d1628]">
                        {q.text}
                        {q.mandatory && <span className="text-[#dc2626] ml-1">*</span>}
                    </div>
                    {(idxs.length > 0 || q.scope === "collective") && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {q.scope === "collective" && <span className="text-[10px] uppercase tracking-wider bg-[#0d1628] text-white px-2 py-0.5">Coletivo</span>}
                            {idxs.map((n) => <span key={n} className="text-xs text-[#b89b6f]">{n}</span>)}
                        </div>
                    )}
                </div>
            </div>
            <div className="pl-11">
                {q.type === "scale_1_5" && (
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button"
                                onClick={() => onChange(n)}
                                className={`phase-dot ${value === n ? "active" : ""}`}
                                data-testid={`scale-${q.id}-${n}`}>{n}</button>
                        ))}
                        {value !== undefined && <CheckCircle2 size={16} className="text-[#10b981] ml-2 mt-2" />}
                    </div>
                )}
                {q.type === "single_choice" && (
                    <div className="space-y-2">
                        {(q.options || []).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name={`q-${q.id}`} checked={value === opt} onChange={() => onChange(opt)}
                                    data-testid={`opt-${q.id}-${opt}`} />
                                <span className="text-[#0d1628]">{opt}</span>
                            </label>
                        ))}
                    </div>
                )}
                {q.type === "multi_choice" && (
                    <div className="space-y-2">
                        {(q.options || []).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={Array.isArray(value) && value.includes(opt)}
                                    onChange={() => onToggleMulti(opt)} data-testid={`opt-${q.id}-${opt}`} />
                                <span className="text-[#0d1628]">{opt}</span>
                            </label>
                        ))}
                    </div>
                )}
                {q.type === "number" && (
                    <input className="input-clean max-w-[200px]" type="number" value={value || ""}
                        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                        data-testid={`num-${q.id}`} />
                )}
                {q.type === "text" && (
                    <textarea className="input-clean" rows={3} value={value || ""}
                        onChange={(e) => onChange(e.target.value)} data-testid={`text-${q.id}`} />
                )}
            </div>
        </div>
    );
}
